import { Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement } from 'react';
import NoResults from '../../../../components/NoResults';
import { CompanyDocuments } from '../../../../models/CompanyDocuments';
import GroupedTableColumnFilters from '../GroupedTableColumnFilters/GroupedTableColumnFilters';
import GroupedTableColumnHeader from '../GroupedTableColumnHeader/GroupedTableColumnHeader';
import GroupedTableRow from '../GroupedTableRow';
import { TableConfiguration } from '../GroupedTableRow/GroupedTableRow.types';
import styles from './DocumentsTable.module.scss';

const cx = classNames.bind(styles);

interface DocumentsTableProps<TData, TSort> {
  config: TableConfiguration<TData>;
  data: CompanyDocuments<TData> | undefined;
  isLoading: boolean;
  onSort: (dataKey: string) => void;
  sortBy: TSort;
  sortDesc: boolean;
  dataTestId?: string;
  noCaret?: boolean;
  filters?: Record<string, any>;
  onFilter?: (key: string, value: string | number) => void;
}

const DocumentsTable = <TData extends object, TSort>({
  config,
  data,
  isLoading,
  onSort,
  sortBy,
  sortDesc,
  dataTestId,
  noCaret,
  filters,
  onFilter,
}: DocumentsTableProps<TData, TSort>): ReactElement => {
  const empty = !isLoading && isEmpty(data?.results);

  if (empty && (!config.isFilterable || isEmpty(filters))) {
    return <NoResults />;
  }

  return (
    <>
      <div className={cx('body', { 'is-loading': isLoading && !config.isFilterable })}>
        {isLoading && !config.isFilterable ? (
          <Loader position="center" />
        ) : (
          <table className={cx('table')} cellSpacing="0" data-testid={dataTestId}>
            <thead>
              <GroupedTableColumnHeader
                config={config}
                onSort={onSort}
                sortBy={sortBy}
                sortDesc={sortDesc}
                noCaret={noCaret}
              />
              {config.isFilterable ? (
                <GroupedTableColumnFilters
                  config={config}
                  data={data}
                  noCaret={noCaret}
                  filters={filters}
                  onFilter={onFilter}
                />
              ) : null}
            </thead>
            {data?.results.map((group, groupIndex) => (
              <GroupedTableRow
                key={config.getRowKey(group[0][0])}
                documents={group}
                tableConfiguration={config}
                groupIndex={groupIndex}
                noCaret={noCaret}
              />
            ))}
          </table>
        )}
      </div>
      {empty && !isLoading ? <NoResults /> : null}
      {isLoading ? <Loader position="center" /> : null}
    </>
  );
};

export default DocumentsTable;
